// Toolkit Chart Component — ag-charts-community backend.
//
// THIS IS THE ONLY FILE IN THE APP THAT MAY IMPORT `ag-charts-community` (never
// `ag-charts-enterprise` — unlicensed enterprise use is a watermark/licensing liability,
// spec 010 Decision 4, Hiram-HARD). `chart_component_controller.ts` never imports ag-charts
// directly; it only sees the neutral types and the `ChartHandle` interface exported below.
//
// FR-029 is enforced by test/components/admin/ai/no_direct_chart_backend_test.rb — zero
// `ag-charts`/`agCharts`/`AgCharts` references may appear in app/components/admin/ai/.
import { AgCharts, ModuleRegistry, AllCommunityModule } from "ag-charts-community";
import type { AgCartesianChartOptions, AgCartesianSeriesOptions } from "ag-charts-community";

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Neutral spec ────────────────────────────────────────────────────────────────────────
// Mirrors Application::Toolkit::ChartComponent#chart_config exactly. Nothing ag-charts-shaped
// crosses this boundary in either direction.

export type NeutralChartType = "line" | "bar" | "area" | "sparkline";

export interface NeutralAxisConfig {
  title?: string;
  format?: string;
  min?: number;
  max?: number;
  stacked?: boolean;
}

export type NeutralPoint = [number | string, number];

export interface NeutralSeriesConfig {
  name: string;
  points: NeutralPoint[];
}

export interface NeutralChartConfig {
  type: NeutralChartType;
  series: NeutralSeriesConfig[];
  x_axis: NeutralAxisConfig;
  y_axis: NeutralAxisConfig;
  title: string;
  description: string;
}

// Opaque handle — deliberately NOT `AgChartInstance`. Nothing ag-charts-shaped leaks upward.
export interface ChartHandle {
  destroy(): void;
}

// ── Internal semantic palette ──────────────────────────────────────────────────────────
// Hex values MIRRORED from the app's design tokens (Sass variables aren't reachable from
// TS, so this is a deliberate, documented copy — never a raw ag-charts theme):
//   app/assets/stylesheets/bitters/_variables.scss   -> $main-color (= $blue-med)
//   app/assets/stylesheets/marketing/_global.scss    -> $blue-dark, $orange-primary,
//                                                        $error-color, $success-color
// $incomplete-color / $payment-authenticating-color (bitters/_variables.scss) are reused
// here purely as extra hues for a 6-color qualitative palette, not for their election-status
// meaning.
const PALETTE: readonly string[] = [
  "#172837", // primary   ($main-color / $blue-med)
  "#FF8002", // accent    ($orange-primary)
  "#17BF3C", // success   ($success-color)
  "#F60020", // error     ($error-color)
  "#06AED5", // info      ($incomplete-color)
  "#E07B00", // warning   ($payment-authenticating-color)
];

// Non-color encoding for line/area series — accessibility floor: encoding must not rely on
// color alone.
const DASH_PATTERNS: ReadonlyArray<number[] | undefined> = [undefined, [6, 3], [2, 2], [8, 2, 2, 2]];
const MARKER_SHAPES: readonly string[] = ["circle", "square", "diamond", "triangle", "cross"];

function colorFor(index: number): string {
  return PALETTE[index % PALETTE.length];
}

function dashFor(index: number): number[] | undefined {
  return DASH_PATTERNS[index % DASH_PATTERNS.length];
}

function markerFor(index: number): string {
  return MARKER_SHAPES[index % MARKER_SHAPES.length];
}

function toDatum(points: NeutralPoint[]): Array<{ x: number | string; y: number }> {
  return points.map(([x, y]) => ({ x, y }));
}

function buildLineOrAreaSeries(
  config: NeutralChartConfig,
  seriesType: "line" | "area"
): AgCartesianSeriesOptions[] {
  const stacked = !!config.y_axis?.stacked;

  return config.series.map((series, index) => {
    const color = colorFor(index);
    const base: Record<string, unknown> = {
      type: seriesType,
      data: toDatum(series.points),
      xKey: "x",
      yKey: "y",
      yName: series.name,
      stroke: color,
      lineDash: dashFor(index),
      stacked,
      marker: {
        enabled: true,
        shape: markerFor(index),
        fill: color,
        stroke: color,
      },
    };
    if (seriesType === "area") {
      base.fill = color;
      base.fillOpacity = 0.25;
    }
    return base as unknown as AgCartesianSeriesOptions;
  });
}

function buildBarSeries(config: NeutralChartConfig): AgCartesianSeriesOptions[] {
  const stacked = !!config.y_axis?.stacked;

  return config.series.map((series, index) => ({
    type: "bar",
    data: toDatum(series.points),
    xKey: "x",
    yKey: "y",
    yName: series.name,
    fill: colorFor(index),
    stacked,
  })) as unknown as AgCartesianSeriesOptions[];
}

function buildSparklineSeries(config: NeutralChartConfig): AgCartesianSeriesOptions[] {
  const first = config.series[0];
  if (!first) return [];

  return [
    {
      type: "line",
      data: toDatum(first.points),
      xKey: "x",
      yKey: "y",
      yName: first.name,
      stroke: colorFor(0),
      marker: { enabled: false },
    },
  ] as unknown as AgCartesianSeriesOptions[];
}

function buildSeries(config: NeutralChartConfig): AgCartesianSeriesOptions[] {
  switch (config.type) {
    case "bar":
      return buildBarSeries(config);
    case "area":
      return buildLineOrAreaSeries(config, "area");
    case "sparkline":
      return buildSparklineSeries(config);
    case "line":
    default:
      return buildLineOrAreaSeries(config, "line");
  }
}

function buildAxis(
  position: "bottom" | "left",
  domainType: "number" | "category",
  axisConfig: NeutralAxisConfig,
  hidden: boolean
) {
  if (hidden) {
    return {
      type: domainType,
      position,
      label: { enabled: false },
      line: { enabled: false },
      gridLine: { enabled: false },
    };
  }

  return {
    type: domainType,
    position,
    title: axisConfig.title ? { enabled: true, text: axisConfig.title } : undefined,
    min: axisConfig.min,
    max: axisConfig.max,
    label: axisConfig.format ? { format: axisConfig.format } : undefined,
  };
}

function buildOptions(container: HTMLElement, config: NeutralChartConfig): AgCartesianChartOptions {
  const isSparkline = config.type === "sparkline";
  const xDomainType: "number" | "category" = config.type === "bar" ? "category" : "number";

  const options: AgCartesianChartOptions = {
    container,
    title: isSparkline ? undefined : { enabled: true, text: config.title },
    legend: { enabled: !isSparkline && config.series.length > 1 },
    series: buildSeries(config),
    axes: [
      buildAxis("bottom", xDomainType, config.x_axis || {}, isSparkline),
      buildAxis("left", "number", config.y_axis || {}, isSparkline),
    ],
  } as unknown as AgCartesianChartOptions;

  return options;
}

export function renderChart(container: HTMLElement, config: NeutralChartConfig): ChartHandle {
  const instance = AgCharts.create(buildOptions(container, config));

  return {
    destroy: () => instance.destroy(),
  };
}

export function destroyChart(handle: ChartHandle | null | undefined): void {
  handle?.destroy();
}
